import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { GAME_TURN_LIMIT } from '../../../../shared/types';
import { api, session } from '../../shell/bridge';
import { Pressable } from '../../ui/Pressable';
import { GuessMePanel } from '../../games/GuessMePanel';
import './game.css';

/** Closed list — mirrors the worker's categories (card 5IXdtz7BL3CX).
 *  The three WYR modes are categories too (card DbcAi0izvJln), so they
 *  answer keep/discard and the rotation like any other game. */
const CATEGORIES: { key: string; label: string }[] = [
  { key: 'places', label: 'Places to visit' },
  { key: 'restaurants', label: 'Restaurants' },
  { key: 'would-you-rather', label: 'Would you rather' },
  { key: 'this-or-that', label: 'This or that' },
  { key: 'memory', label: 'Memory prompts' },
  { key: 'wyr-silly', label: 'Would you rather · Silly' },
  { key: 'wyr-spicy', label: 'Would you rather · Spicy' },
  { key: 'wyr-deep', label: 'Would you rather · Deep' },
];

/** Today's game (card vsDgyzDbgtxh): one date-seeded pick so the choice
 *  burden disappears — both partners see the same recc on the same day, it
 *  changes daily, and it never lands on the same mode two days running when
 *  the pool allows it. Pure function of the calendar, no server state. */
function todaysPick(pool: string[]): string | null {
  if (pool.length === 0) return null;
  const hash = (day: number) => {
    let h = Math.imul(day ^ 0x9e3779b9, 0x85ebca6b);
    h ^= h >>> 13;
    return Math.abs(h);
  };
  const today = Math.floor(Date.now() / 86_400_000);
  let idx = hash(today) % pool.length;
  if (pool.length > 1) {
    const yesterday = hash(today - 1) % pool.length;
    if (yesterday === idx) idx = (idx + 1) % pool.length;
  }
  return pool[idx];
}

/** Closing copy (card cNGAFvkiRCGz): time-of-day from the LOCAL clock. */
function daypart(): 'morning' | 'afternoon' | 'evening' {
  const h = new Date().getHours();
  return h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
}

interface GameRoundView {
  roundId: string;
  category: string;
  question: string;
  state: 'open' | 'revealed' | 'closed';
  answeredBy: string[];
  myAnswer: string | null;
  myPassed: boolean;
  partnerAnswer: string | null;
  partnerPassed: boolean;
  rating: 'keep' | 'discard' | null;
  /** WYR A-or-B options — null on a topic round (card DbcAi0izvJln). */
  options: [string, string] | null;
  /** Revealed rounds in the current session (card cNGAFvkiRCGz). */
  turns: number;
}

interface GamePanelProps {
  members: { memberId: string; displayName: string }[];
}

/**
 * Back-and-forth topic round (card 5IXdtz7BL3CX) — the concierge's first
 * two-player surface. Both partners answer the same prompt; neither answer
 * shows until both have answered (or passed), then they appear side by side
 * and "next topic" starts the next round.
 */
export function GamePanel({ members }: GamePanelProps) {
  const me = session();
  const [round, setRound] = useState<GameRoundView | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [category, setCategory] = useState('places');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Today's game (card vsDgyzDbgtxh): the recc pool is the category list
     minus what the couple discarded — a mode they retired must not come back
     as the recommendation. Ratings are the same Plus-gated fetch the round
     itself is; if they fail the pool degrades to every category. */
  const [ratings, setRatings] = useState<Record<string, string> | null>(null);
  /* Closing card dismissed for this reveal (card cNGAFvkiRCGz) — a quiet
     exit that leaves the reveal readable. Reload brings it back. */
  const [closingHidden, setClosingHidden] = useState(false);

  const partner = members.find(m => m.memberId !== me?.memberId);
  const partnerName = partner?.displayName ?? 'your partner';
  const nameOf = (memberId: string) =>
    members.find(m => m.memberId === memberId)?.displayName ?? 'Someone';

  const reload = useCallback(async () => {
    try {
      const data = await api<{ round: GameRoundView | null }>('/api/game/round');
      setRound(data.round ?? null);
      setError(null);
    } catch (e) {
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        console.error('[Pear] game load failed', e);
      }
    } finally {
      setLoaded(true);
    }
    try {
      const given = await api<{ ratings: Record<string, string> }>('/api/game/ratings');
      setRatings(given.ratings ?? {});
    } catch (e) {
      // An abort is the panel unmounting mid-fetch — navigation, not a fault.
      if (!(e instanceof DOMException && e.name === 'AbortError')) {
        console.error('[Pear] game ratings load failed', e);
      }
      setRatings(r => r ?? {});
    }
  }, []);

  /* The recc itself: today's pick over the un-discarded pool (card
     vsDgyzDbgtxh). Recomputed every render is pointless, so it hangs off the
     ratings fetch — same-day stable, changes at local midnight. */
  const recc = useMemo(() => {
    const all = CATEGORIES.map(c => c.key);
    const pool = ratings
      ? all.filter(k => ratings[k] !== 'discard')
      : all;
    return todaysPick(pool.length > 0 ? pool : all);
  }, [ratings]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Live: the partner's answer, or their "next topic", arrives over the room
  // socket as t:'game' and useThread turns it into this event.
  useEffect(() => {
    const onGame = () => void reload();
    window.addEventListener('pear:game', onGame);
    return () => window.removeEventListener('pear:game', onGame);
  }, [reload]);

  /* catOverride: undefined = use the chip (existing behaviour), null = send
     no category so the server's rotation picks ("Play another" should feel
     fresh, not pinned to whatever chip was last lit). */
  const start = async (advance: boolean, catOverride?: string | null) => {
    if (busy) return;
    setBusy(true);
    try {
      const chosen = catOverride === null ? undefined : (catOverride ?? category);
      const data = await api<{ round: GameRoundView }>('/api/game/round', {
        body: chosen ? { category: chosen, advance } : { advance },
      });
      setRound(data.round);
      setDraft('');
      setError(null);
      setClosingHidden(false);
    } catch (e) {
      console.error('[Pear] game start failed', e);
      setError('Could Not Start a Round — Try Again.');
    } finally {
      setBusy(false);
    }
  };

  /* WYR pick (card DbcAi0izvJln): the answer IS the option string, so the
     same /api/game/answer route carries it and the server validates it
     against the round's shipped pair. */
  const pick = async (option: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await api<{ round: GameRoundView }>('/api/game/answer', { body: { text: option } });
      setRound(data.round);
      setError(null);
    } catch (e) {
      console.error('[Pear] game pick failed', e);
      setError('That Did Not Save — Try Again.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      const data = await api<{ round: GameRoundView }>('/api/game/answer', { body: { text } });
      setRound(data.round);
      setDraft('');
      setError(null);
    } catch (err) {
      console.error('[Pear] game answer failed', err);
      setError('That Did Not Save — Try Again.');
    } finally {
      setBusy(false);
    }
  };

  const pass = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await api<{ round: GameRoundView }>('/api/game/pass', { body: {} });
      setRound(data.round);
      setDraft('');
    } catch (e) {
      console.error('[Pear] game pass failed', e);
    } finally {
      setBusy(false);
    }
  };

  // Keep / discard a finished round (card OJmoL0w1fGrm), couple-level.
  const rate = async (verdict: 'keep' | 'discard') => {
    if (busy || !round) return;
    setBusy(true);
    try {
      const data = await api<{ round: GameRoundView }>('/api/game/rate', {
        body: { roundId: round.roundId, verdict },
      });
      setRound(data.round);
    } catch (e) {
      console.error('[Pear] game rate failed', e);
      setError('That Did Not Save — Try Again.');
    } finally {
      setBusy(false);
    }
  };

  const iAnswered = !!round && (round.answeredBy.includes(me?.memberId ?? '') || round.myPassed);
  const partnerAnswered = !!round && round.answeredBy.some(id => id !== me?.memberId);
  const revealed = round?.state === 'revealed';
  /* WYR (card DbcAi0izvJln): an options-bearing round is the A-or-B game. */
  const isWyr = !!round?.options;
  const picked = isWyr && revealed && !round?.myPassed && !round?.partnerPassed;
  const matched = picked && round?.myAnswer != null && round.myAnswer === round.partnerAnswer;
  /* Closing card (card cNGAFvkiRCGz): at the turn limit on a revealed round,
     the closing replaces the "start/next" affordance — the session is over
     until someone chooses another one. */
  const sessionOver = revealed && !!round && round.turns >= GAME_TURN_LIMIT;

  return (
    <>
    <section className="game-panel" id="game-panel" aria-labelledby="game-title">
      {/* Heading discipline (Jonathan 2026-10-17): every sub-panel in Life and
          Us opens on a real h2 of its own, the same way Requests, Chores,
          Inbox, Plan and Digest do — this one was an h3 inside a section with
          no accessible name. Same id, so every walk selector still resolves. */}
      <h2 className="game-title" id="game-title">
        Back and forth
      </h2>

      {!loaded && <p className="game-hint">Loading…</p>}

      {loaded && !round && (
        <>
          {/* Today's game (card vsDgyzDbgtxh): ONE recommendation, one tap —
              the manual picker stays below for couples who want to choose.
              Lead with the recc so the panel opens on an answer, not a menu. */}
          {recc && (
            <div className="game-today" id="game-today" data-test="game-today">
              <p className="game-today-label">Today's game</p>
              <p className="game-today-name">
                {CATEGORIES.find(c => c.key === recc)?.label ?? recc}
              </p>
              <Pressable
                className="pear-btn pear-btn-mustard game-today-play"
                id="btn-game-today"
                disabled={busy}
                onClick={() => void start(false, recc)}
              >
                Play today's game
              </Pressable>
            </div>
          )}
          <p className="game-hint" id="game-empty">
            Pick a topic, you both answer, then you see each other's take. Nobody sees anything until
            both of you have written.
          </p>
          <div className="game-cats" role="group" aria-label="Topic category">
            {CATEGORIES.map(c => (
              <button
                key={c.key}
                type="button"
                aria-pressed={category === c.key}
                className={`game-cat${category === c.key ? ' is-on' : ''}`}
                onClick={() => setCategory(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>
          <Pressable
            className="pear-btn pear-btn-mustard game-start"
            id="btn-game-start"
            disabled={busy}
            onClick={() => void start(false)}
          >
            Start a round
          </Pressable>
        </>
      )}

      {loaded && round && (
        <>
          <p className="game-category" id="game-category">
            {CATEGORIES.find(c => c.key === round.category)?.label ?? round.category}
          </p>
          <p className="game-question" id="game-question">
            {round.question}
          </p>

          {!revealed && (
            <>
              {iAnswered ? (
                <p className="game-status" id="game-answered">
                  {isWyr && round.myAnswer ? `You picked: ${round.myAnswer}. ` : ''}
                  Your answer is in.{' '}
                  {partnerAnswered ? `Waiting for ${partnerName} to answer…` : `Waiting for ${partnerName} to answer.`}
                </p>
              ) : isWyr && round.options ? (
                /* WYR (card DbcAi0izvJln): the round ships its two options —
                   a pick, not prose. One button each, then Pass. */
                <div className="game-wyr" role="group" aria-label="Your pick">
                  {round.options.map((opt, i) => (
                    <button
                      key={opt}
                      type="button"
                      className="pear-btn game-wyr-opt"
                      id={`game-wyr-${i === 0 ? 'a' : 'b'}`}
                      disabled={busy}
                      onClick={() => void pick(opt)}
                    >
                      {opt}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="pear-btn game-pass"
                    id="btn-game-pass"
                    disabled={busy}
                    onClick={() => void pass()}
                  >
                    Pass
                  </button>
                </div>
              ) : (
                <form className="game-answer" onSubmit={submit}>
                  <input
                    id="game-answer"
                    className="pear-chat-input game-input"
                    placeholder="Your answer…"
                    aria-label="Your answer"
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                  />
                  <div className="game-actions">
                    <button
                      type="submit"
                      className="pear-btn pear-btn-mustard"
                      id="btn-game-answer"
                      disabled={busy || draft.trim().length === 0}
                    >
                      Send
                    </button>
                    <button
                      type="button"
                      className="pear-btn game-pass"
                      id="btn-game-pass"
                      disabled={busy}
                      onClick={() => void pass()}
                    >
                      Pass
                    </button>
                  </div>
                </form>
              )}
              {!iAnswered && partnerAnswered && (
                <p className="game-status" id="game-waiting">
                  {partnerName} has answered — your turn.
                </p>
              )}
            </>
          )}

          {revealed && (
            <div className="game-reveal" id="game-reveal">
              <div className="game-side">
                <span className="game-who">{me?.memberId ? nameOf(me.memberId) : 'You'}</span>
                <p className="game-line" id="game-mine">
                  {round.myPassed ? 'Passed' : (round.myAnswer ?? '—')}
                </p>
              </div>
              <div className="game-side">
                <span className="game-who">{partnerName}</span>
                <p className="game-line" id="game-partner">
                  {round.partnerPassed ? 'Passed' : (round.partnerAnswer ?? '—')}
                </p>
              </div>
            </div>
          )}

          {/* WYR reveal verdict (card DbcAi0izvJln): a match is the whole
              point of the round — say it plainly. A pass leaves no verdict. */}
          {revealed && picked && (
            <p className={`game-match${matched ? ' is-match' : ''}`} id="game-match" data-match={String(!!matched)}>
              {matched ? 'Same pick — you matched.' : 'Different picks — that is half the fun.'}
            </p>
          )}

          {/* Closing card (card cNGAFvkiRCGz): the session's last turn is
              revealed, so the closing copy takes the place of the next-round
              button. "Play another" resets the counter server-side by opening
              a fresh round; "Not now" folds the card away for this reveal. */}
          {sessionOver && !closingHidden && (
            <div className="game-closing" id="game-closing" data-test="game-closing">
              <p className="game-closing-copy">
                That concludes this round. Feel free to play another, and enjoy the rest of your{' '}
                {daypart()} together.
              </p>
              <div className="game-closing-actions">
                <button
                  type="button"
                  className="pear-btn pear-btn-mustard"
                  id="btn-game-another"
                  disabled={busy}
                  onClick={() => void start(true, null)}
                >
                  Play another
                </button>
                <button
                  type="button"
                  className="pear-btn game-pass"
                  id="btn-game-closing-dismiss"
                  onClick={() => setClosingHidden(true)}
                >
                  Not now
                </button>
              </div>
            </div>
          )}

          {revealed && (
            <div className="game-rating" id="game-rating">
              {round.rating === null ? (
                <>
                  <p className="game-rating-prompt">Was this one a keeper?</p>
                  <div className="game-rating-actions">
                    <button
                      type="button"
                      id="btn-game-keep"
                      className="pear-btn game-rating-btn"
                      disabled={busy}
                      onClick={() => void rate('keep')}
                    >
                      + Keep this game
                    </button>
                    <button
                      type="button"
                      id="btn-game-discard"
                      className="pear-btn game-rating-btn game-rating-discard"
                      disabled={busy}
                      onClick={() => void rate('discard')}
                    >
                      − Not for us
                    </button>
                  </div>
                </>
              ) : (
                <p className="game-rating-done" id="game-rating-done">
                  {round.rating === 'keep'
                    ? `Keeping this one — it will come around again.`
                    : `Noted — we will stop offering this one.`}
                </p>
              )}
            </div>
          )}

          {/* The closing card IS the next-round affordance at the turn limit
              (card cNGAFvkiRCGz) — two buttons for one job would read as a
              choice the product does not offer. */}
          {!(sessionOver && !closingHidden) && (
            <div className="game-actions">
              <Pressable
                className="pear-btn pear-btn-blush"
                id="btn-game-next"
                disabled={busy}
                onClick={() => void start(true)}
              >
                Next topic
              </Pressable>
            </div>
          )}
        </>
      )}

      {error && (
        <p className="game-error" role="alert" id="game-error">
          {error}
        </p>
      )}
    </section>

    {/* Guess Me (card eq92fFXqQUuP) — the two-player round engine, its own
        section under Back and forth so both games share one Us tab. */}
    <GuessMePanel members={members} />
    </>
  );
}
